import { useEffect, useRef, useState, type ReactElement } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import type { ILokaliseKey } from '~/types/catalog'

import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import { useOnClickOutside } from '~/hooks/useOnClickOutside'

import './styles.scss'

interface Props {
  disabled?: boolean
  lokaliseKeys: ILokaliseKey[]
  name: string
  onNbTranslationsChange?: (nbTranslations: number) => void
}

const LokaliseKeyInput = ({ disabled, lokaliseKeys, name, onNbTranslationsChange }: Props): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext()

  const lokaliseKeyWatch = watch('lokaliseKey')

  const optionsRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(optionsRef, () => setShowOptions(false))

  const [filteredOptions, setFilteredOptions] = useState<ILokaliseKey[]>([])

  const [showOptions, setShowOptions] = useState(false)

  const lokaliseKeyFilteredOptions = (value: string) => {
    if (value && lokaliseKeys.length > 0) {
      const filtered = lokaliseKeys.filter(({ key }) => key.toLowerCase().includes(value.toLowerCase()))
      setFilteredOptions(filtered)
      setShowOptions(true)
    } else {
      setShowOptions(false)
    }
  }

  /** Handlers */

  const handleOptionClick = (option: ILokaliseKey, field: any) => () => {
    field.onChange(option.key)
    field.value = option.key
    onNbTranslationsChange && onNbTranslationsChange(option.nb_translations)
    setShowOptions(false)
  }

  /** Effects */

  useEffect(() => {
    if (lokaliseKeyWatch === '' && onNbTranslationsChange) onNbTranslationsChange(0)
  }, [lokaliseKeyWatch, onNbTranslationsChange])

  /** Render */

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="lokalise-key-input">
          <TextInput
            {...field}
            autoComplete="off"
            disabled={disabled}
            error={errors.lokaliseKey?.message as string}
            onChange={(e) => {
              field.onChange(e)
              lokaliseKeyFilteredOptions(e.target.value)
            }}
            placeholder={lokaliseKeys.length === 0 ? 'Loading...' : 'Lokalise key'}
            size="small"
          />
          {showOptions && filteredOptions.length > 0 ? (
            <div className="lokalise-key-input__options" ref={optionsRef}>
              {filteredOptions.map((option, idx) => (
                <div className="option" key={idx} onClick={handleOptionClick(option, field)}>
                  {option.key}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
    />
  )
}

export default LokaliseKeyInput
